export const navLinks = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  { label: "Services", path: "/services" },
  { label: "Athletes", path: "/athletes" },
  { label: "Partnerships", path: "/partnerships" },
];

export const services = [
  {
    slug: "athlete-talent-management",
    title: "Athlete & Talent Management",
    description:
      "We represent and manage athletes end-to-end, focusing on their growth, positioning, long-term career development, and handling their social media presence and brand collaborations.",
    deliverables: [
      "Brand endorsements & ambassadorships",
      "Athlete-led content & storytelling",
      "Contract negotiations and talent curation",
    ],
    case_study: {
      title: "Deyan Shah Athlete Growth Program",
      subtitle: "Track and field athlete represented by Purnam since 1 July 2025",
      media: {
        label: "Featured Athlete",
        url: "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/jpn28pg9_Deyan.jpeg",
        size: "large",
      },
      challenge: [
        "Brand positioning was weak and lacked a clear athlete-first narrative.",
        "Commercial opportunities were limited, with very few active brand partnerships.",
        "There was no consistent growth engine connecting social visibility with deal flow.",
      ],
      strategy: [
        "Built a stronger brand position for Deyan focused on performance, discipline, and credibility.",
        "Aligned social media narrative with commercial categories that match athlete trust.",
        "Activated focused commercial outreach to convert visibility into recurring paid collaborations.",
      ],
      execution: [
        "Executed brand positioning and social strategy with consistency in content direction and posting rhythm.",
        "Managed end-to-end commercial conversations, collaboration onboarding, and delivery support.",
        "Secured partnerships across highly varied categories, proving Deyan’s versatility as a brand-facing athlete.",
        "Representative collaborations include SuperYou, MuscleBlaze, Kapiva, Supply6, Seekacause, Lair Man, XStep, Zixa, and ab coffee.",
      ],
      brand_partners: [
        "SuperYou",
        "MuscleBlaze",
        "Seekacause",
        "Lair Man",
        "XStep",
        "Oxphecker",
        "Supply6",
        "Zixa",
        "NeuLife",
        "Smashfit",
        "ab coffee",
        "Kapiva",
        "A29",
      ],
      outcomes: [
        { label: "Instagram Growth", value: "11,000 to 17,000 followers (+6,000 / +54.5%)." },
        { label: "Average Views", value: "25,000 to 60,000 (+140%)." },
        { label: "Brand Collaborations", value: "13 partnerships delivered in one year." },
        { label: "Commercial Value", value: "Approx. ₹1.5 lakh in brand deals within one year." },
        { label: "Versatility Signal", value: "Worked successfully with brands across nutrition, wellness, skincare, grooming, apparel, and F&B categories." },
      ],
      brand_takeaway:
        "For athletes, this validates that the right management partner can accelerate both visibility and monetization. For brands, it shows that Purnam delivers credible athlete-led storytelling with stronger campaign trust and execution reliability.",
    },
  },
  {
    slug: "sports-esports-ip-management",
    title: "Sports & Esports IP Management",
    description:
      "Strategic integrations with leagues, teams, tournaments, and fan communities across sport and gaming.",
    deliverables: [
      "League & tournament partnerships",
      "On-ground, digital and hybrid activations",
      "End-to-end campaign execution",
    ],
    case_study: {
      title: "Global Sports Pickleball League Partnership Program",
      subtitle: "Brand partnerships in one of India’s fastest-growing emerging sports IPs",
      media: {
        label: "Featured IP",
        url: "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/4a15bsjx_IG%20purnam%20%282%29.png",
        size: "medium",
      },
      reference_images: [
        "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/2i3sgxwv_Case%20study%202.png",
        "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/1fvn4ui5_Case%20study2.png",
      ],
      challenge: [
        "Establish GSPL as a high-value brand partnership platform through visibility, community engagement, and long-term association.",
        "Create sponsor confidence in an emerging sport where category memory is still being formed.",
        "Ensure delivery quality across every partner touchpoint for repeat participation.",
      ],
      strategy: [
        "Used an early-mover partnership strategy in a rapidly scaling pickleball ecosystem, especially in Mumbai.",
        "Positioned GSPL as a cultural relevance engine with broad demographic appeal across age and income segments.",
        "Prioritized partner trust and over-delivery to convert one-time sponsors into long-term multi-IP collaborators.",
      ],
      execution: [
        "Secured a strong mix of national and regional sponsors for the latest edition.",
        "Managed end-to-end brand partnership execution with added value beyond promised deliverables.",
        "Activated brands through experience-led integrations rather than passive logo placements.",
        "Onboarded partners across beverages, hospitality, and lifestyle categories.",
        "Maintained a seamless, high-trust partner experience throughout the tournament lifecycle.",
      ],
      outcomes: [
        { label: "Partner Satisfaction", value: "Brands received everything promised and more, driving strong perceived ROI." },
        { label: "Renewal Signal", value: "Delivery-first execution led to renewals for future GSPL editions." },
        { label: "Commercial Expansion", value: "One-time sponsors evolved into multi-IP brand partners beyond GSPL." },
        { label: "Market Positioning", value: "GSPL strengthened its standing as an early-mover platform for emerging sports brands." },
        { label: "Brand Takeaway", value: "Emerging sports IPs unlock outsized visibility and community goodwill with flawless execution partners." },
      ],
    },
  },
  {
    slug: "brand-management-strategy",
    title: "Brand Management & Strategy",
    description:
      "A retainer-based growth model where Purnam acts like your extended sports and wellness execution team.",
    deliverables: [
      "Monthly/quarterly goal-based activation",
      "Creator/talent shortlisting + brand approvals",
      "Transparent reporting, optimisation, and delivery",
    ],
    case_study: {
      title: "SuperYou Athlete-Led Yeast Protein Launch",
      subtitle: "Brand management and strategy through athlete-first storytelling for a new-age nutrition product",
      media: {
        label: "Featured Brand",
        url: "/brand-logos/superyou.png",
      },
      reference_images: [
        "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/5w2rogwp_Case%20study%201.png",
        "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/99mvu4hs_Case%20study1.png",
      ],
      challenge: [
        "Introduce SuperYou’s yeast-based protein to the Indian market with strong trust signals around taste, easy digestion, and clean nutrition.",
        "Build category confidence in a relatively new nutrition format where credibility is crucial for adoption.",
        "Create an athlete-led market entry narrative that resonates with everyday consumers.",
      ],
      strategy: [
        "Built a premium talent mix centered on athlete validation as the primary trust engine.",
        "Designed a roster model of 80–85% professional athletes and 15–20% wellness experts.",
        "Aligned messaging to product benefits: taste, gut-ease, and clean nutrition.",
      ],
      execution: [
        "Curated and shared a master shortlist with the brand, then finalized talent collaboratively.",
        "Executed end-to-end production: scripting, briefing, filming direction, raw handover, and posting workflows.",
        "Drove multi-city, multi-sport representation to improve relevance across diverse audience clusters.",
        "Delivered 12 professional athletes and 3 wellness creators as the core campaign talent output.",
      ],
      outcomes: [
        { label: "Estimated Reach", value: "400K–600K+" },
        { label: "Engagement Rate", value: "5.8%, above the typical benchmark for Instagram Reels." },
        { label: "Shares", value: "4,100+, indicating strong product curiosity and audience interest." },
        { label: "Trust Impact", value: "Athlete-led storytelling built early trust for a new protein category." },
        { label: "Brand Takeaway", value: "Strategic athlete-led narratives outperform generic influencer campaigns for credibility-led nutrition adoption." },
      ],
    },
  },
];

export const processSteps = [
  "Share Your Goals",
  "We Build the Plan",
  "Brand Approves",
  "End-to-End Execution",
  "Report & Optimise",
];

export const deckPillars = [
  "Athlete & Talent Network",
  "Sports & Esports IPs",
  "Creator Ecosystem",
  "Brand Execution",
];

export const caseStudies = [
  {
    title: "SuperYou - Athlete-Led Launch",
    summary: "Athlete-led campaign for yeast protein positioning focused on trust, education, and conversion.",
  },
  {
    title: "Global Sports Pickleball League",
    summary: "Brand-partner activations across sports IP with integrated on-ground and digital storytelling.",
  },
];

export const athletes = [
  {
    id: "ATH000",
    name: "Gurindervir Singh",
    sport: "Track & Field",
    bio: "India’s fastest man",
    achievements:
      "1) Broke India’s 100m national record with 10.09s, becoming the first Indian under 10.10. 2) Won gold in men’s 100m at the 2026 Athletics Federation Cup (Ranchi). 3) Part of India’s 4x100m relay national-record team (38.69) in 2025.",
    instagram_url: "https://www.instagram.com/gurindervir_singh/",
    image: "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/bhcuv30f_INDIA%2C%20your%20new%20fastest%20man%20alive%21%F0%9F%87%AE%F0%9F%87%B3.jpg",
    image_position: "52% 18%",
  },
  {
    id: "ATH001",
    name: "Devindar Valmiki",
    sport: "Hockey",
    bio: "Indian field hockey midfielder",
    achievements:
      "1) Participated in the 2016 Summer Olympics. 2) Silver medal at the 2016 Champions Trophy. 3) Gold at the 2016 Asian Champions Trophy.",
    instagram_url: "https://www.instagram.com/devindar/",
    image: "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/3ie0q1km_Devindar%20valmiki.png",
    image_position: "50% 2%",
  },
  {
    id: "ATH002A",
    name: "Jyothi Yarraji",
    sport: "Track & Field",
    bio: "Indian Track and Field Athlete",
    achievements:
      "1) Arjuna Award winner (2024). 2) Won gold in the 100m hurdles at the Asian Athletics Championships. 3) National Record holder in the women’s 100m hurdles.",
    instagram_url: "https://www.instagram.com/jyothi_yarraji_/?hl=en",
    image: "https://customer-assets-0z36b82j.emergentagent.net/job_athlete-hub-88/artifacts/4qi8ea7q_IMG_3393.jpeg",
    image_position: "50% 18%",
  },
  {
    id: "ATH002",
    name: "Sakshi Chavan",
    sport: "Track & Field",
    bio: "Indian track and field athlete",
    achievements:
      "1) Participated in the Junior Asian Games. 2) Silver medalist at the South Asian Championships. 3) 100m U-14 national record holder.",
    instagram_url: "https://www.instagram.com/sakshichavan__/",
    image: "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/alefsanc_Sakshi.JPG",
  },
  {
    id: "ATH003",
    name: "Lokendra Singh",
    sport: "Basketball",
    bio: "Indian basketball player",
    achievements:
      "1) Represented India’s senior national team in the FIBA Basketball World Cup Asian Qualifiers. 2) Represented India in the FIBA U18 Asian Championship 2022. 3) Continued involvement in qualifiers for the FIBA World Cup 2027.",
    instagram_url: "https://www.instagram.com/lokendra.ss/",
    image: "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/vt4sy0s3_Lokendra.jpeg",
    image_position: "50% 14%",
  },
  {
    id: "ATH003A",
    name: "Arvinder Singh",
    sport: "Basketball",
    bio: "Indian Basketball Player",
    achievements:
      "1) A two-time Senior National Champion. 2) Won gold at the 72nd Senior National Championship (2022). 3) Won gold at the 75th Senior National Championship (2026).",
    instagram_url: "https://www.instagram.com/arvinderkahlon22/?hl=en",
    image: "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/lto8hedv_WhatsApp%20Image%202026-07-02%20at%204.18.13%20PM.jpeg",
    image_position: "50% 8%",
  },
  {
    id: "ATH004",
    name: "Vaibhavi Deshmukh",
    sport: "Padel",
    bio: "Indian padel player",
    achievements:
      "1) India No. 1 Women’s Padel Player. 2) 5× Women’s National Champion. 3) Bronze medalist at the Asia Pacific Padel Cup 2024.",
    instagram_url: "https://www.instagram.com/vee.deshmukh/",
    image: "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/u5y3kq01_image.png",
    image_position: "50% 46%",
  },
  {
    id: "ATH024",
    name: "Pavana Nagaraj",
    sport: "Track & Field",
    bio: "Indian Track & Field Athlete",
    achievements:
      "1) Gold medalist at the Asian U20 Athletics Championships (Long Jump). 2) Junior National Champion. 3) Junior Federation Cup Champion.",
    instagram_url: "https://www.instagram.com/_pavana_ath/?hl=en",
    image: "https://customer-assets-0z36b82j.emergentagent.net/job_athlete-hub-88/artifacts/9p5qnz40_IMG_8614.JPG.webp",
    image_position: "50% 16%",
  },
  {
    id: "ATH005",
    name: "Manan Vohra",
    sport: "Cricket",
    bio: "Indian cricketer",
    achievements:
      "1) He has crossed 10,000 runs in domestic cricket across all formats. 2)scored 1,000+ runs in the IPL 3)Crucial 67-run knock in the 2014 IPL Final.",
    instagram_url: "https://www.instagram.com/mananvohra36/",
    image: "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/sgviqbjt_Manan.PNG",
    image_position: "55% 22%",
  },
  {
    id: "ATH006",
    name: "Menika Thakur",
    sport: "Handball",
    bio: "Team India Handball player",
    achievements:
      "1) Bronze medalist at the 7th Asian Women’s Club League Handball Championship (2024). 2) Part of the India women’s national handball team at the 20th Asian Women’s Handball Championship (2024). 3) Participated in the 19th Asian Games held in Hangzhou, China (2023).",
    instagram_url: "https://www.instagram.com/menika_777/",
    image: "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/7o47xzm7_Menika.PNG",
  },
  {
    id: "ATH007",
    name: "Ritika Thakur",
    sport: "Heptathlon",
    bio: "Haryana’s Heptathlon powerhouse",
    achievements:
      "1)multiple golds and silvers at the Haryana State Athletics Championship 2)gold at the North Zone National Athletics Championship 3)representing in 2 All India Inter University Championships",
    instagram_url: "https://www.instagram.com/ritika__.thakur/",
    image: "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/x4wov00o_Ritika.jpeg",
    image_position: "50% 4%",
  },
  {
    id: "ATH008",
    name: "Yashita Bakshi",
    sport: "Cycling",
    bio: "Indian Cyclist",
    achievements:
      "1) Three-time Chandigarh State Champion. 2) Represented India at the Khelo India Youth Games 2025. 3) Secured a Top 7 finish at the National Championships.",
    instagram_url: "https://www.instagram.com/yashitabakshi/?hl=en",
    image: "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/obzmmjwm_WhatsApp%20Image%202026-07-02%20at%203.26.02%20PM.jpeg",
    image_position: "52% 20%",
  },
  {
    id: "ATH009",
    name: "Aryan Brahmin",
    sport: "Handball",
    bio: "Team India handball player",
    achievements:
      "1) Played for India at the Asian Men’s Beach Handball Championship. 2) Won silver as part of the Indian team at the Independence Cup International Handball Tournament. 3) One of Odisha’s prominent international handball players, representing India in both indoor and beach handball formats.",
    instagram_url: "https://www.instagram.com/aryan.brahmin/",
    image: "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/1u5ignir_Aryan%20B.jpeg",
    image_position: "50% 18%",
  },
  {
    id: "ATH010",
    name: "Sahil Jadhav",
    sport: "Cricket",
    bio: "rising star in Indian Cricket",
    achievements:
      "1) T20 Mumbai League Season 3 Champion 2)Best Wicketkeeper in the U-16 Kalpesh Koli Tournament 3)Best Batsman in the U-19 KC Mahindra Shield.",
    instagram_url: "https://www.instagram.com/__sahiljadhav__/",
    image: "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/u0gv74dp_sahil.JPG",
    image_position: "60% 22%",
  },
  {
    id: "ATH011",
    name: "Deyan Shah",
    sport: "Sprinter",
    bio: "Rising Sprinter",
    achievements:
      "1) Competed in the men’s Under-23 400m run and placed third. 2) Active in competitive track events at the junior and Under-23 level.",
    instagram_url: "https://www.instagram.com/deyann_18/",
    image: "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/mkz2a7ca_Deyan.jpeg",
    image_position: "50% 18%",
  },
  {
    id: "ATH012",
    name: "Harsh Sharma",
    sport: "Badminton",
    bio: "national badminton player",
    achievements: "1)representing Maharashtra in the Under-13 Men’s Doubles 2)Under-19 Mixed Doubles State Champion.",
    instagram_url: "https://www.instagram.com/harshhsharmaaa/",
    image: "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/yhs00mdx_Harsh.png",
    image_position: "50% 14%",
  },
  {
    id: "ATH013A",
    name: "Prashant Singh Rawat",
    sport: "Basketball",
    bio: "Indian basketball player",
    achievements:
      "1) National Basketball Championship winner (2017). 2) Part of the India U18 team that competed at the Asian Championship in Thailand. 3) Part of the India squad that won gold at the South Asian Basketball Championship.",
    instagram_url: "https://www.instagram.com/rawat15_/",
    image: "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/ygf8lzeb_Prashant.jpeg",
    image_position: "50% 14%",
  },
  {
    id: "ATH013",
    name: "Lamine Moro",
    sport: "Football",
    bio: "experienced Ghanaian centre-back",
    achievements:
      "1) Played professionally across Africa, Europe, and Asia, including clubs like Al-Quwa Al-Jawiya, Liberty Professionals, and Young Africans S.C. 2) Solid stint with Buildcon FC: 47 appearances and 3 goals. 3) Featured in India’s I-League with Real Kashmir FC, scoring in his debut season; also earned 11 caps for Ghana U-20.",
    instagram_url: "https://www.instagram.com/laminebinomar/",
    image: "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/oz145u54_lamine.png",
    image_position: "45% 18%",
  },
  {
    id: "ATH014",
    name: "Rohan Kapoor",
    sport: "Badminton",
    bio: "Indian badminton sensation",
    achievements: "1)silver medal in the 2022 Asian Games 2) Career Best - World no. 29",
    instagram_url: "https://www.instagram.com/16rohan/",
    image: "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/maveo47u_rohan.png",
    image_position: "52% 22%",
  },
  {
    id: "ATH015",
    name: "Shiya Lalwani",
    sport: "Cycling",
    bio: "Indian cyclist",
    achievements: "1) Won the 2024 Track Cycling National Championship 2) Multiple medals in the Khelo India Youth Games",
    instagram_url: "https://www.instagram.com/shiya.lalwani/",
    image: "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/mt6lojwg_Shiya.jpeg",
    image_position: "50% 12%",
  },
  {
    id: "ATH016",
    name: "Sehnaj Singh",
    sport: "Football",
    bio: "Indian Footballer",
    achievements: "1)represented India in the 2015 SAFF Championship and helped them win the Title 2)represented Mohun Bagan in the 2014-2015",
    instagram_url: "https://www.instagram.com/sehnajsingh99/",
    image: "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/ej1zyouj_sehnaj.png",
    image_position: "52% 18%",
  },
  {
    id: "ATH017",
    name: "Gurmukh Singh",
    sport: "Football",
    bio: "Indian Footballer",
    achievements:
      "1) Signed by Chennaiyin FC ahead of the 2022–23 Indian Super League season. 2) Transferred to Sreenidi Deccan FC in 2023 in the I-League, continuing as a regular defensive presence.",
    instagram_url: "https://www.instagram.com/gurmukh.singh0120/",
    image: "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/o9tonnev_Gurmukh.png",
    image_position: "48% 20%",
  },
  {
    id: "ATH018",
    name: "Prakriti Ranjan",
    sport: "Basketball",
    bio: "Basketball Player",
    achievements:
      "1) Won third place at the 72nd Senior National Basketball Championship (2022) with her team. 2) Represented India/Delhi in 3x3 national competitions and youth championships, including Under-17. 3) Competed in the All-India University Games Basketball event and appears in official team rosters/FIBA profiles.",
    instagram_url: "https://www.instagram.com/prakritiiiii_r/",
    image: "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/q3ea8hxz_Ritika.png",
    image_position: "50% 20%",
  },
  {
    id: "ATH019",
    name: "Mahek Nayak",
    sport: "Badminton",
    bio: "national level badminton player",
    achievements:
      "1) Gold (Mixed Doubles) and Bronze (Women’s Team) at the Maharashtra State Olympic Games. 2) Competed internationally, including the Guwahati Masters 2023 (Women’s Doubles). 3) Played in national events like the Yonex-Sunrise All India Senior Ranking Tournaments.",
    instagram_url: "https://www.instagram.com/maheknayakk/",
    image: "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/36lplrxm_mahek.png",
    image_position: "50% 14%",
  },
  {
    id: "ATH020",
    name: "Akashdeep Singh",
    sport: "Football",
    bio: "rising star in Indian football",
    achievements:
      "1) Made his senior debut for the India national team in 2018 and has earned three international caps. 2) Began his ISL career with Mumbai City FC (2017–18) and later joined Chennaiyin FC for the 2021–22 season. 3) Currently plays as a defender for Namdhari FC in the I-League.",
    instagram_url: "https://www.instagram.com/akashkahlon1/",
    image: "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/zkj5a2uf_Akashdeep.png",
    image_position: "50% 16%",
  },
  {
    id: "ATH021",
    name: "Davinder Singh",
    sport: "Football",
    bio: "Indian Footballer",
    achievements:
      "1) Debuted for the Indian national team in 2018 and has three international caps. 2) Played in the ISL with Mumbai City FC and later Chennaiyin FC (2021–22 season). 3) Currently a key defender for Namdhari FC in the I-League.",
    instagram_url: "https://www.instagram.com/davinder_singh_32/",
    image: "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/r1kycwey_davinder.png",
    image_position: "50% 16%",
  },
  {
    id: "ATH022",
    name: "Germanpreet Singh",
    sport: "Football",
    bio: "Indian Footballer",
    achievements:
      "1) Represented India at senior level (2016–2019) and also played for U-23 and U-19 national teams. 2) Made 55+ ISL appearances with Chennaiyin FC (2018–2022), contributing to a championship-winning squad. 3) Currently plays for Jamshedpur FC (2022–present), continuing his ISL midfield career.",
    instagram_url: "https://www.instagram.com/german_singh28/",
    image: "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/yns007ek_Germanpreet.jpeg",
    image_position: "44% 20%",
  },
  {
    id: "ATH023",
    name: "Shikha and Ashwini",
    sport: "Badminton",
    bio: "Indian women badminton doubles players",
    achievements:
      "1) Current World No. 52 and India No. 1 (Women’s doubles category). 2) Dutch International Series champions and Maldives International runner-up. 3) Multiple BWF International Series and Challenge semifinals and quarterfinals.",
    instagram_links: [
      { label: "Shikha", url: "https://www.instagram.com/gautamshikha_/" },
      { label: "Ashwini", url: "https://www.instagram.com/ashwini__.bhat/" },
    ],
    image: "https://customer-assets.emergentagent.com/job_athlete-hub-88/artifacts/09bmv7fy_image.png",
    image_position: "50% 18%",
  },
];

export const partnerLogos = [
  {
    name: "Muscle Blaze",
    logoUrl: "/brand-logos/muscle-blaze.png",
    website: "https://www.muscleblaze.com",
  },
  { name: "SuperYou", logoUrl: "/brand-logos/superyou.png", website: "https://www.superyou.in" },
  {
    name: "Kapiva",
    logoUrl: "/brand-logos/kapiva.png",
    website: "https://www.kapiva.in",
  },
  {
    name: "Decathlon",
    logoUrl: "/brand-logos/decathlon.png",
    website: "https://www.decathlon.in",
  },
  {
    name: "A29",
    logoUrl: "/brand-logos/a29.png",
    website: "https://www.a29wellbeing.com",
  },
  {
    name: "The Func. Lab",
    logoUrl: "/brand-logos/func-lab.png",
    website: "https://www.thefunclab.com",
  },
  { name: "TenxYou", logoUrl: "/brand-logos/tenxu.png", website: "https://www.tenxyou.com" },
  { name: "Neuro", logoUrl: "/brand-logos/neuro.png", website: "https://www.neurogumindia.com" },
  { name: "Wellbeing Nutrition", logoUrl: "/brand-logos/wellbeing-nutrition.png", website: "https://www.wellbeingnutrition.com" },
  {
    name: "Lakme",
    logoUrl: "/brand-logos/lakme.png",
    website: "https://www.lakmeindia.com",
  },
  {
    name: "AceBlend",
    logoUrl: "/brand-logos/aceblend.png",
    website: "https://www.aceblend.com",
  },
  {
    name: "ab.coffee",
    logoUrl: "/brand-logos/ab-coffee.png",
    website: "https://www.abcoffee.in",
  },
  { name: "Radico", logoUrl: "/brand-logos/radico.png", website: "https://radicokhaitan.com" },
  {
    name: "GNC",
    logoUrl: "/brand-logos/gnc.png",
    website: "https://www.guardian.in",
  },
  { name: "Whatsup Wellness", logoUrl: "/brand-logos/whatsup-wellness.png", website: "https://whatsupwellness.in" },
  { name: "Supply6", logoUrl: "/brand-logos/supply6.png", website: "https://supplysix.com" },
  {
    name: "Jimmy's",
    logoUrl: "/brand-logos/jimmys.png",
    website: "https://drinkjimmys.com",
  },
];

export const partnerBrands = partnerLogos.map((brand) => brand.name);

export const socialLinks = [
  { label: "Instagram", href: "https://www.instagram.com/purnamtalentmanagement/" },
  { label: "LinkedIn", href: "https://www.linkedin.com/company/purnamtalentmanagement/" },
  { label: "X", href: "https://x.com/Purnamtalent" },
];